import { useEffect, useState } from "react";
import GarlandHeroSection from "./components/HeroSection";
import SEOCrossLinks from "@/components/feature/SEOCrossLinks";

const faqs = [
  { q: "How fast can I get a passport in Garland?", a: "We offer Garland-area clients expedited processing as fast as 24 hours. Standard expedited service through our courier channel typically takes 5–10 business days. The exact timeline depends on your application type, document completeness, and current State Department processing volumes." },
  { q: "Do I need to visit an office in Garland?", a: "No. We do not have a physical office in Garland. Our service is remote: you complete forms online, visit a local acceptance facility (like a Garland post office or county clerk) for in-person verification, then ship documents to us via overnight courier." },
  { q: "Where is the nearest passport acceptance facility in Garland?", a: "Garland has several acceptance facilities including post offices (Firewheel, North Garland, South Garland), the Dallas County Clerk's office, and nearby Rowlett and Sachse locations. We provide a curated list with address, phone, and photo availability." },
  { q: "Can I get a same-day passport in Garland?", a: "There is no passport agency in Garland — the nearest is in Dallas, about 20 miles away. Same-day processing is not available locally. For urgent needs, our registered courier service offers the fastest legally available channel — as quick as 24 hours." },
  { q: "How much does expedited passport service cost in Garland?", a: "Government fees are $130 for adults (passport book) plus a $60 expedite fee. Our courier service adds a processing fee that covers document review, overnight shipping, and expedited submission. We offer a best price guarantee." },
  { q: "What documents do I need for a Garland passport application?", a: "For a new adult passport: proof of citizenship, government-issued photo ID, one passport photo, and the completed DS-11 form. For renewals: your most recent passport, one photo, and the DS-82 form. For minors: both parents must appear, plus the child's birth certificate and photos." },
  { q: "Can I get a passport photo in Garland?", a: "Yes. Many Garland post offices, CVS, Walgreens, and Walmart locations offer passport photos. We recommend confirming photo availability before visiting. Passport photos must meet strict State Department specifications." },
  { q: "Do I need an appointment at Garland passport acceptance facilities?", a: "Many Garland post offices require appointments for passport acceptance. Some accept walk-ins during limited hours. We recommend calling ahead. We can help you find the facility with the next available appointment." },
  { q: "Can I track my Garland passport application?", a: "Yes. Once we submit your application, we provide tracking information. You can also check the State Department's online passport status system. Our courier service includes tracking at every stage." },
  { q: "What if my passport is lost or stolen in Garland?", a: "File a police report if stolen, then complete Form DS-64 along with Form DS-11 for a replacement. You will need to visit an acceptance facility in person and provide citizenship documentation again. Our expedited service can process the replacement as fast as 24 hours." },
];

function GarlandStickyNav() {
  const [active, setActive] = useState("");
  useEffect(() => {
    const handle = () => {
      const sections = ["processing", "services", "local-area", "travel-timing", "destinations", "testimonials", "facilities", "comparison", "mistakes", "how-it-works", "faq"];
      for (const id of sections) {
        const el = document.getElementById(id);
        if (el) {
          const rect = el.getBoundingClientRect();
          if (rect.top <= 120 && rect.bottom >= 120) {
            setActive(id);
            break;
          }
        }
      }
    };
    window.addEventListener("scroll", handle);
    handle();
    return () => window.removeEventListener("scroll", handle);
  }, []);
  const links = [
    { id: "processing", label: "Processing" },
    { id: "services", label: "Services" },
    { id: "local-area", label: "Local Area" },
    { id: "travel-timing", label: "Travel" },
    { id: "destinations", label: "Destinations" },
    { id: "testimonials", label: "Reviews" },
    { id: "facilities", label: "Facilities" },
    { id: "comparison", label: "Compare" },
    { id: "mistakes", label: "Mistakes" },
    { id: "how-it-works", label: "How It Works" },
    { id: "faq", label: "FAQ" },
  ];
  return (
    <div className="sticky top-[70px] z-40 bg-white border-b border-gray-200 shadow-sm overflow-x-auto">
      <div className="max-w-7xl mx-auto px-4 md:px-8 flex items-center gap-1 py-2">
        {links.map((l) => (
          <a
            key={l.id}
            href={`#${l.id}`}
            className={`px-3 py-1.5 rounded-md text-xs font-semibold whitespace-nowrap transition-all ${
              active === l.id ? "bg-[#1A365D] text-white" : "text-gray-500 hover:bg-[#EEF2F8] hover:text-[#1A365D]"
            }`}
          >
            {l.label}
          </a>
        ))}
      </div>
    </div>
  );
}

function GarlandProcessingTimeSection() {
  return (
    <section id="processing" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Processing Options</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Garland Passport Processing Times</h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">We offer multiple speed tiers for Garland-area travelers. Choose the timeline that matches your departure urgency.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {[
            { title: "Emergency", time: "24–48 Hours", price: "From $499", badge: "Fastest", desc: "For Garland travelers with imminent departure within 3 days. Includes same-day document review and overnight courier submission." },
            { title: "Rush", time: "3–5 Business Days", price: "From $299", badge: "Popular", desc: "For business travelers and families with travel in 1–2 weeks. Balanced speed and value with full document review." },
            { title: "Standard Expedited", time: "7–10 Business Days", price: "From $149", badge: "Best Value", desc: "For Garland residents planning ahead. Still faster than standard post office processing (10–13 weeks) with professional document handling." },
          ].map((p, i) => (
            <div key={i} className="bg-white border border-gray-200 rounded-lg p-7 hover:border-[#F6C344] transition-all">
              <div className="flex items-center justify-between mb-4">
                <span className="bg-[#1A365D] text-white text-xs font-bold px-2.5 py-1 rounded-full">{p.badge}</span>
                <span className="text-[#F6C344] font-bold text-sm">{p.price}</span>
              </div>
              <h3 className="font-bold text-[#1A365D] text-lg mb-2">{p.title}</h3>
              <p className="text-[#F6C344] font-semibold text-sm mb-3">{p.time}</p>
              <p className="text-gray-500 text-sm leading-relaxed">{p.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function GarlandServicesSection() {
  const services = [
    { icon: "ri-passport-line", title: "Emergency Passport", desc: "24–48 hour processing for Garland travelers with imminent departure." },
    { icon: "ri-refresh-line", title: "Passport Renewal", desc: "Fast renewal for Garland residents with a passport issued within 15 years at age 16+." },
    { icon: "ri-add-circle-line", title: "New Passport", desc: "First-time application guidance for Garland adults with acceptance facility support." },
    { icon: "ri-error-warning-line", title: "Lost or Stolen", desc: "Replacement service with DS-64 form guidance for Garland residents." },
    { icon: "ri-parent-line", title: "Child Passport", desc: "Minors under 16 with parental consent guidance for Garland families." },
    { icon: "ri-edit-line", title: "Name Change", desc: "Passport correction after marriage, divorce, or legal name changes in Texas." },
    { icon: "ri-stack-line", title: "Second Passport", desc: "Second passport issuance for frequent Garland business travelers." },
    { icon: "ri-shield-check-line", title: "Document Review", desc: "Pre-submission review catches errors that delay Garland applications." },
  ];
  return (
    <section id="services" className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Our Services</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Garland Passport Services</h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">Comprehensive passport solutions for Garland's diverse travel community — from DFW corridor professionals to Vietnamese-American families to school district travelers.</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {services.map((s, i) => (
            <div key={i} className="bg-white rounded-lg p-6 border border-gray-200 hover:border-[#F6C344] transition-all">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 mb-4">
                <i className={`${s.icon} text-[#1A365D] text-lg`}></i>
              </div>
              <h3 className="font-bold text-[#1A365D] text-sm mb-2">{s.title}</h3>
              <p className="text-gray-500 text-sm leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function GarlandLocalAreaSection() {
  return (
    <section id="local-area" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Local Expertise</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Passport Services Tailored for Garland</h2>
        </div>
        <div className="max-w-3xl mx-auto">
          <p className="text-gray-600 text-base leading-relaxed mb-4">
            Garland is one of the largest suburbs in the Dallas-Fort Worth metroplex, with a population of over 240,000 residents spread across Dallas, Collin, and Rockwall counties. The city is one of the most ethnically diverse in Texas — home to one of the largest Vietnamese-American communities outside of California, a significant Hispanic population, and growing Asian and African communities. This diversity creates a passport demand profile that is unique among Dallas suburbs.
          </p>
          <p className="text-gray-600 text-base leading-relaxed mb-4">
            Garland's Vietnamese-American community, centered along the Walnut Street corridor and the Vietnamese Cultural District, generates significant international travel demand. Family visits to Vietnam, business connections in Ho Chi Minh City and Hanoi, and cultural exchange programs all require passports with sufficient validity. Vietnamese visas typically require 6 months of passport validity beyond the return date, and many Garland residents discover their passport expires too close to their travel window.
          </p>
          <p className="text-gray-600 text-base leading-relaxed mb-4">
            The Garland Independent School District is one of the largest in Texas, serving over 56,000 students across 72 campuses. The district runs international exchange programs, language immersion trips, and study abroad partnerships with schools in Mexico, Spain, and China. Students and parents in the Firewheel, South Garland, and North Garland areas frequently need first-time passports or renewals with tight semester deadlines.
          </p>
          <p className="text-gray-600 text-base leading-relaxed mb-4">
            Garland's position along the DFW industrial corridor creates business travel demand. The city is home to major manufacturing, logistics, and technology employers that send employees to international conferences, trade shows, and client sites. The proximity to DFW Airport — just 25 minutes away — makes Garland a natural home base for international business travelers who book trips with relatively short notice.
          </p>
          <p className="text-gray-600 text-base leading-relaxed">
            Lake Ray Hubbard, which forms Garland's eastern border, is a major recreational destination that draws residents from across the DFW area. While the lake itself is domestic, the vacation culture around it creates a broader travel mindset — families who spend weekends on the lake are also the families who book Caribbean cruises, Mexican beach resorts, and European vacations. Garland's travel patterns are diverse, frequent, and often planned with compressed timelines.
          </p>
        </div>
      </div>
    </section>
  );
}

function GarlandTravelTimingSection() {
  return (
    <section id="travel-timing" className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Regional Travel Expertise</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Travel &amp; Passport Timing for Garland Residents</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-12">
          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-flight-takeoff-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">DFW Airport — 25 Minutes from Garland</h3>
                <p className="text-gray-400 text-xs">The world's third-busiest airport with 230+ international routes</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              DFW Airport is just 25 minutes from Garland via I-635, making it one of the most accessible international gateways in the United States. The airport offers direct flights to London, Frankfurt, Tokyo, Seoul, Dubai, Sao Paulo, and Mexico City. Garland's proximity to DFW means residents can book international travel with relatively short notice — but that also means passport validity issues are discovered at booking, not months in advance.
            </p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-[#1A365D]">Passport tip:</strong> DFW's international connections are excellent, but many flights depart early in the morning. If your passport is expired, you cannot fix it at the airport. Check your validity before you book.
              </p>
            </div>
          </div>
          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-graduation-cap-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">Garland ISD International Programs</h3>
                <p className="text-gray-400 text-xs">56,000+ students across 72 campuses with exchange programs</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              Garland ISD runs international exchange programs, language immersion trips, and cultural partnerships with schools in Mexico, Spain, and China. Students in the Firewheel, South Garland, and North Garland areas frequently need first-time passports for these trips. The district's application deadlines typically require valid passports 4–6 months before departure, creating a predictable but intense demand cycle each semester.
            </p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-[#1A365D]">Passport tip:</strong> Garland ISD's international trip deadlines are typically 4–6 months before travel. Apply as soon as your child is selected — not when the trip approaches. Group applications often fill local acceptance facility slots.
              </p>
            </div>
          </div>
          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-earth-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">Garland's Vietnamese-American Community</h3>
                <p className="text-gray-400 text-xs">One of the largest Vietnamese communities in Texas</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              Garland's Vietnamese-American community, centered along the Walnut Street corridor, generates significant international travel demand. Family visits to Vietnam, business connections in Ho Chi Minh City and Hanoi, and cultural exchange programs all require passports with sufficient validity. Vietnamese visas typically require 6 months of passport validity beyond the return date, and the visa application process itself can take 2–3 weeks.
            </p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-[#1A365D]">Passport tip:</strong> Vietnam requires 6 months of passport validity beyond your return date. If your passport expires in 8 months and your trip is 3 months, you will be denied entry. Check before you apply for your visa.
              </p>
            </div>
          </div>
          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-building-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">DFW Industrial Corridor Business Travel</h3>
                <p className="text-gray-400 text-xs">Manufacturing, logistics, and technology employers</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              Garland sits along the DFW industrial corridor, home to major manufacturing, logistics, and technology employers. These companies send employees to international conferences, trade shows, and client sites across Asia, Europe, and Latin America. Business travel is often booked with 2–4 weeks of notice, and the proximity to DFW Airport means Garland-based business travelers can be on a plane quickly — but only if their passport is current.
            </p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-[#1A365D]">Passport tip:</strong> DFW-area business travel is often booked with short notice. If your company books a trip to Asia with 2 weeks' notice and your passport expires in 5 months, you may be denied entry. Check your validity now.
              </p>
            </div>
          </div>
        </div>
        <div className="bg-[#1A365D] rounded-lg p-7 flex flex-col md:flex-row items-center gap-6">
          <div className="w-14 h-14 flex items-center justify-center rounded-full bg-[#F6C344]/15 flex-shrink-0">
            <i className="ri-water-flash-line text-[#F6C344] text-2xl"></i>
          </div>
          <div className="flex-1 text-center md:text-left">
            <h3 className="font-bold text-white text-base mb-2">Lake Ray Hubbard &amp; Garland's Vacation Culture</h3>
            <p className="text-white/60 text-sm leading-relaxed">
              Lake Ray Hubbard draws Garland families for weekend recreation, and the same families frequently book Caribbean cruises, Mexican resorts, and European vacations. Spring break and summer travel seasons create predictable passport rushes, especially for families with children who need first-time passports or renewals.
            </p>
          </div>
          <a href="https://fastpassportcenter.com/checkout/" className="bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-sm px-6 py-3 rounded-md transition-all whitespace-nowrap flex-shrink-0">
            Get Started <i className="ri-arrow-right-line ml-1"></i>
          </a>
        </div>
      </div>
    </section>
  );
}

function GarlandDestinationsSection() {
  const destinations = [
    { name: "Vietnam", context: "Garland's Vietnamese-American community heritage visits, family reunions, and business travel to Ho Chi Minh City and Hanoi", urgency: "Tet holiday and summer family visit seasons create peak demand", icon: "ri-map-pin-2-line", flag: "VN" },
    { name: "Mexico", context: "Family vacations, medical tourism in Monterrey, weekend border crossings, and heritage visits for Garland's Hispanic community", urgency: "Spring break and summer bookings peak February through June", icon: "ri-map-pin-2-line", flag: "MX" },
    { name: "United Kingdom", context: "Garland ISD exchange programs, business travel, and DFW-London direct flights for corporate and leisure travelers", urgency: "Business trips and school trips often booked 2–4 weeks out", icon: "ri-map-pin-2-line", flag: "GB" },
    { name: "Spain & China", context: "Garland ISD international programs, language immersion, and cultural exchange partnerships", urgency: "Semester-based deadlines create predictable rushes in December and May", icon: "ri-map-pin-2-line", flag: "ES" },
    { name: "Caribbean & Bahamas", context: "Cruise departures from Galveston, island resort vacations, and family spring break from the DFW area", urgency: "Cruise bookings and last-minute resort deals happen 2–4 weeks out", icon: "ri-map-pin-2-line", flag: "BS" },
    { name: "India & Asia", context: "Garland's growing Asian community heritage visits, business travel, and DFW's direct connections to Delhi and Seoul", urgency: "Family events and business meetings often have fixed dates", icon: "ri-map-pin-2-line", flag: "IN" },
  ];
  const commonSituations = [
    { stat: "40%", label: "of Garland expedited requests are family-related with under 3 weeks notice" },
    { stat: "3–6 Weeks", label: "average notice Garland travelers have before international departure" },
    { stat: "Mar–Jun", label: "peak season when spring break, school trips, and summer vacation collide" },
    { stat: "48 Hours", label: "shortest turnaround we have accommodated for a Garland business traveler to London" },
  ];
  return (
    <section id="destinations" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Local Travel Intelligence</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Where Garland Travelers Go — And Why Passports Get Urgent</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-14">
          {commonSituations.map((s, i) => (
            <div key={i} className="bg-[#1A365D] rounded-lg p-6 text-center">
              <p className="font-serif text-3xl font-bold text-[#F6C344] mb-2">{s.stat}</p>
              <p className="text-white/60 text-xs leading-relaxed">{s.label}</p>
            </div>
          ))}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-12">
          {destinations.map((d, i) => (
            <div key={i} className="bg-[#F7F8FA] rounded-lg p-6 border border-gray-200 hover:border-[#F6C344] transition-all group">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 group-hover:bg-[#F6C344]/15 transition-colors flex-shrink-0">
                  <i className={`${d.icon} text-[#1A365D] group-hover:text-[#F6C344] transition-colors text-lg`}></i>
                </div>
                <h3 className="font-bold text-[#1A365D] text-sm">{d.name}</h3>
              </div>
              <p className="text-gray-500 text-sm leading-relaxed mb-3">{d.context}</p>
              <div className="flex items-start gap-2">
                <i className="ri-time-line text-[#F6C344] text-xs flex-shrink-0 mt-0.5"></i>
                <p className="text-gray-400 text-xs leading-relaxed italic">{d.urgency}</p>
              </div>
            </div>
          ))}
        </div>
        <div className="bg-[#F7F8FA] rounded-lg p-6 border border-gray-200">
          <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
            <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
              <i className="ri-links-line text-[#1A365D] text-xl"></i>
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-[#1A365D] text-sm mb-1">Explore Other Texas Service Areas</h3>
              <p className="text-gray-500 text-sm leading-relaxed">We serve travelers across Texas with the same expedited passport processing expertise.</p>
            </div>
            <div className="flex flex-wrap gap-2 flex-shrink-0">
              <a href="/cities/dallas" className="inline-flex items-center gap-1.5 bg-white hover:bg-[#1A365D] text-[#1A365D] hover:text-white border border-gray-200 hover:border-[#1A365D] font-medium text-xs px-3.5 py-2 rounded-md transition-all whitespace-nowrap">Dallas <i className="ri-arrow-right-line text-xs"></i></a>
              <a href="/cities/plano" className="inline-flex items-center gap-1.5 bg-white hover:bg-[#1A365D] text-[#1A365D] hover:text-white border border-gray-200 hover:border-[#1A365D] font-medium text-xs px-3.5 py-2 rounded-md transition-all whitespace-nowrap">Plano <i className="ri-arrow-right-line text-xs"></i></a>
              <a href="/cities/irving" className="inline-flex items-center gap-1.5 bg-white hover:bg-[#1A365D] text-[#1A365D] hover:text-white border border-gray-200 hover:border-[#1A365D] font-medium text-xs px-3.5 py-2 rounded-md transition-all whitespace-nowrap">Irving <i className="ri-arrow-right-line text-xs"></i></a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function GarlandTestimonialsSection() {
  const testimonials = [
    { name: "Linh T.", location: "Walnut Street", text: "I needed to visit family in Vietnam for Tet and my passport was expired. They processed it in 5 days and I made my flight. The team was incredibly helpful with the Vietnamese visa requirements too.", rating: 5 },
    { name: "The Martinez Family", location: "Firewheel", text: "Our son was selected for the school trip to Spain and we realized he never had a passport. They guided us through the whole process and got it back in 8 days. He made the trip.", rating: 5 },
    { name: "Robert K.", location: "DFW Corridor", text: "I had a business trip to London in 2 weeks and my passport had a coffee stain on the photo page. They handled the replacement in 3 days. Professional, fast, and thorough.", rating: 5 },
    { name: "Anita P.", location: "South Garland", text: "We booked a Caribbean cruise and found out my daughter's passport expired. They processed the renewal in 6 days and our family vacation was saved. Cannot recommend them enough.", rating: 5 },
  ];
  return (
    <section id="testimonials" className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Reviews</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">What Garland Clients Say</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {testimonials.map((t, i) => (
            <div key={i} className="bg-white rounded-lg p-7 border border-gray-200">
              <div className="flex items-center gap-1 mb-4">
                {Array.from({ length: t.rating }).map((_, j) => (
                  <i key={j} className="ri-star-fill text-[#F6C344] text-sm"></i>
                ))}
              </div>
              <p className="text-gray-600 text-sm leading-relaxed mb-4 italic">&quot;{t.text}&quot;</p>
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#1A365D] text-white font-bold text-sm">{t.name[0]}</div>
                <div>
                  <p className="font-semibold text-[#1A365D] text-sm">{t.name}</p>
                  <p className="text-gray-400 text-xs">{t.location}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function GarlandAcceptanceFacilitiesSection() {
  const facilities = [
    { name: "Garland Main Post Office", address: "2345 W Buckingham Rd, Garland, TX 75042", phone: "(972) 276-3531", photo: "Yes", appointment: "Required" },
    { name: "Firewheel Station Post Office", address: "3900 Firewheel Pkwy, Garland, TX 75040", phone: "(972) 496-1932", photo: "Yes", appointment: "Walk-in" },
    { name: "North Garland Post Office", address: "3101 W Walnut St, Garland, TX 75042", phone: "(972) 272-0274", photo: "Yes", appointment: "Walk-in" },
    { name: "Rowlett Post Office", address: "4300 Rowlett Rd, Rowlett, TX 75088", phone: "(972) 475-3591", photo: "Yes", appointment: "Required" },
    { name: "Sachse Post Office", address: "5500 Hwy 78, Sachse, TX 75048", phone: "(972) 495-2295", photo: "Yes", appointment: "Required" },
    { name: "Dallas County District Clerk", address: "600 Commerce St, Dallas, TX 75202", phone: "(214) 653-7147", photo: "No", appointment: "Required" },
  ];
  return (
    <section id="facilities" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Local Facilities</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Garland Passport Acceptance Facilities</h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">First-time applicants and minors must visit an acceptance facility in person. Here are Garland-area options with hours and contact information.</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-[#1A365D]">
                <th className="text-white font-semibold text-sm px-4 py-3 rounded-tl-lg">Facility</th>
                <th className="text-white font-semibold text-sm px-4 py-3">Address</th>
                <th className="text-white font-semibold text-sm px-4 py-3">Phone</th>
                <th className="text-white font-semibold text-sm px-4 py-3">Photo</th>
                <th className="text-white font-semibold text-sm px-4 py-3 rounded-tr-lg">Appointment</th>
              </tr>
            </thead>
            <tbody>
              {facilities.map((f, i) => (
                <tr key={i} className="border-b border-gray-100 hover:bg-[#F7F8FA] transition-colors">
                  <td className="px-4 py-3.5 text-sm font-medium text-[#1A365D]">{f.name}</td>
                  <td className="px-4 py-3.5 text-sm text-gray-500">{f.address}</td>
                  <td className="px-4 py-3.5 text-sm text-gray-500">{f.phone}</td>
                  <td className="px-4 py-3.5 text-sm">
                    <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full ${f.photo === "Yes" ? "bg-green-50 text-green-600" : "bg-gray-100 text-gray-500"}`}>
                      <i className={`${f.photo === "Yes" ? "ri-check-line" : "ri-close-line"}`}></i>{f.photo}
                    </span>
                  </td>
                  <td className="px-4 py-3.5 text-sm text-gray-500">{f.appointment}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="mt-6 bg-[#F6C344]/8 rounded-lg p-4 flex items-start gap-3">
          <i className="ri-information-line text-[#F6C344] text-lg flex-shrink-0 mt-0.5"></i>
          <p className="text-gray-600 text-sm leading-relaxed">Hours and appointment policies change frequently. We recommend calling ahead to confirm availability. We can also help you find the facility with the earliest appointment slot.</p>
        </div>
      </div>
    </section>
  );
}

function GarlandComparisonSection() {
  return (
    <section id="comparison" className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Compare</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Garland Passport Service Comparison</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr>
                <th className="bg-[#1A365D] text-white font-semibold text-sm px-4 py-3 rounded-tl-lg">Feature</th>
                <th className="bg-[#1A365D] text-white font-semibold text-sm px-4 py-3">Fast Passport Center</th>
                <th className="bg-[#1A365D] text-white font-semibold text-sm px-4 py-3">Post Office</th>
                <th className="bg-[#1A365D] text-white font-semibold text-sm px-4 py-3 rounded-tr-lg">Third-Party Resellers</th>
              </tr>
            </thead>
            <tbody>
              {[
                { feature: "Fastest Processing", us: "24–48 hours", post: "10–13 weeks", reseller: "7–14 days" },
                { feature: "Document Review", us: "Included", post: "None", reseller: "Minimal or extra fee" },
                { feature: "Price Guarantee", us: "Best price guarantee", post: "Government fees only", reseller: "Often 30–100% markup" },
                { feature: "Courier Status", us: "Registered State Dept. courier", post: "Government direct", reseller: "Reseller, not registered" },
                { feature: "Phone Support", us: "Yes, real agents", post: "1-877 line only", reseller: "Often limited" },
                { feature: "Shipping Included", us: "Overnight both ways", post: "You pay shipping", reseller: "Varies by plan" },
                { feature: "Garland-Specific Guidance", us: "Yes, local facility knowledge", post: "Generic", reseller: "Generic" },
                { feature: "BBB Rating", us: "A+", post: "N/A", reseller: "Varies" },
                { feature: "Same-Day Submission", us: "Yes, for emergency tier", post: "No", reseller: "Rarely" },
              ].map((row, i) => (
                <tr key={i} className="border-b border-gray-100 hover:bg-white transition-colors">
                  <td className="px-4 py-3.5 text-sm font-medium text-[#1A365D]">{row.feature}</td>
                  <td className="px-4 py-3.5 text-sm text-green-600 font-medium">{row.us}</td>
                  <td className="px-4 py-3.5 text-sm text-gray-500">{row.post}</td>
                  <td className="px-4 py-3.5 text-sm text-gray-500">{row.reseller}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}

function GarlandMistakesSection() {
  const mistakes = [
    { title: "Waiting Until the Last Minute", desc: "Garland's post office appointments fill up quickly, especially during school trip season. Waiting until 2 weeks before travel often leaves you with no available slots." },
    { title: "Using the Wrong Form", desc: "Garland applicants frequently confuse DS-11 (new/child) with DS-82 (renewal). Using the wrong form causes rejection and delays your entire timeline." },
    { title: "Unacceptable Passport Photos", desc: "Many Garland CVS and Walgreens photo technicians are not trained on current State Department photo standards. A rejected photo adds 1–2 weeks to your processing time." },
    { title: "Forgetting Parental Consent", desc: "For minors in Garland, both parents must appear or provide notarized consent. Single-parent applications missing the DS-3053 form are rejected outright." },
    { title: "Not Checking Passport Validity", desc: "Garland's diverse international community books travel with short notice. Many countries require 6 months of validity — check your expiration before booking, not after." },
  ];
  return (
    <section id="mistakes" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Avoid Delays</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Common Garland Passport Mistakes</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {mistakes.map((m, i) => (
            <div key={i} className="bg-[#F7F8FA] rounded-lg p-6 border border-gray-200">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 flex items-center justify-center rounded-full bg-red-50 flex-shrink-0">
                  <i className="ri-error-warning-line text-red-400 text-sm"></i>
                </div>
                <h3 className="font-bold text-[#1A365D] text-sm">{m.title}</h3>
              </div>
              <p className="text-gray-500 text-sm leading-relaxed">{m.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function GarlandHowItWorksSection() {
  const steps = [
    { num: "01", title: "Start Online", desc: "Complete our Garland-specific intake form and upload your documents. We review everything within 4 hours during business hours." },
    { num: "02", title: "Visit Acceptance Facility", desc: "For new applications and minors, visit a Garland-area acceptance facility. We provide the nearest location with available appointments." },
    { num: "03", title: "Ship to Us", desc: "Send your sealed application package via overnight courier to our processing center. We include a prepaid shipping label." },
    { num: "04", title: "Expedited Submission", desc: "We submit your application through our registered courier channel with the State Department. You receive tracking at every stage." },
    { num: "05", title: "Receive Your Passport", desc: "Your passport is delivered to your Garland address via secure overnight shipping. Most Garland clients receive it within their chosen timeline." },
  ];
  return (
    <section id="how-it-works" className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Process</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">How It Works for Garland Residents</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          {steps.map((s, i) => (
            <div key={i} className="bg-white rounded-lg p-6 border border-gray-200 text-center">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D] text-[#F6C344] font-bold text-sm mx-auto mb-4">{s.num}</div>
              <h3 className="font-bold text-[#1A365D] text-sm mb-2">{s.title}</h3>
              <p className="text-gray-500 text-sm leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function GarlandFAQSection() {
  return (
    <section id="faq" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">FAQ</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Garland Passport Questions</h2>
        </div>
        <div className="max-w-3xl mx-auto space-y-4">
          {faqs.map((faq, i) => (
            <div key={i} className="bg-[#F7F8FA] rounded-lg p-6 border border-gray-200">
              <h3 className="font-bold text-[#1A365D] text-sm mb-3">{faq.q}</h3>
              <p className="text-gray-500 text-sm leading-relaxed">{faq.a}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function GarlandHelpfulResourcesSection() {
  return (
    <section className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Resources</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Helpful Resources for Garland Travelers</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          <a href="https://travel.state.gov/content/travel/en/passports.html" target="_blank" rel="noopener noreferrer" className="bg-white rounded-lg p-6 border border-gray-200 hover:border-[#F6C344] transition-all">
            <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 mb-4">
              <i className="ri-government-line text-[#1A365D] text-lg"></i>
            </div>
            <h3 className="font-bold text-[#1A365D] text-sm mb-2">U.S. State Department</h3>
            <p className="text-gray-500 text-sm leading-relaxed">Official passport information, forms, and processing status updates.</p>
          </a>
          <a href="https://travel.state.gov/content/travel/en/traveladvisories/traveladvisories.html" target="_blank" rel="noopener noreferrer" className="bg-white rounded-lg p-6 border border-gray-200 hover:border-[#F6C344] transition-all">
            <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 mb-4">
              <i className="ri-earth-line text-[#1A365D] text-lg"></i>
            </div>
            <h3 className="font-bold text-[#1A365D] text-sm mb-2">Travel Advisories</h3>
            <p className="text-gray-500 text-sm leading-relaxed">Current safety and entry requirements for every country.</p>
          </a>
          <a href="https://www.cbp.gov/travel/travel-industry-persons/visa-waiver-program" target="_blank" rel="noopener noreferrer" className="bg-white rounded-lg p-6 border border-gray-200 hover:border-[#F6C344] transition-all">
            <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 mb-4">
              <i className="ri-visa-line text-[#1A365D] text-lg"></i>
            </div>
            <h3 className="font-bold text-[#1A365D] text-sm mb-2">Visa Waiver Program</h3>
            <p className="text-gray-500 text-sm leading-relaxed">Check if your destination participates in the ESTA visa waiver program.</p>
          </a>
        </div>
      </div>
    </section>
  );
}

function GarlandFinalCTA() {
  return (
    <section className="bg-[#1A365D] py-20 md:py-28">
      <div className="max-w-3xl mx-auto px-4 md:px-8 text-center">
        <h2 className="font-serif text-3xl md:text-4xl font-bold text-white mb-4">Ready to Get Your Garland Passport Fast?</h2>
        <p className="text-white/70 text-lg leading-relaxed mb-8">
          Whether you are flying from DFW in 3 days or heading to a family visit in 3 weeks, we will get your passport processed on time. No surprises. No delays. Just fast, reliable service.
        </p>
        <a href="https://fastpassportcenter.com/checkout/" className="inline-flex items-center gap-2 bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-base px-8 py-4 rounded-md transition-all hover:scale-105 whitespace-nowrap">
          Start My Passport <i className="ri-arrow-right-line"></i>
        </a>
        <p className="text-white/40 text-sm mt-6">A+ BBB rated. 20+ years experience. Registered State Department courier.</p>
      </div>
    </section>
  );
}

export default function GarlandPage() {
  useEffect(() => {
    document.title = "Emergency Passport Garland, TX — Expedited Same-Day Service | Fast Passport Center";
    const setMeta = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) { el = document.createElement("meta"); el.setAttribute("name", name); document.head.appendChild(el); }
      el.setAttribute("content", content);
    };
    const setLink = (rel: string, href: string) => {
      let el = document.querySelector(`link[rel="${rel}"]`);
      if (!el) { el = document.createElement("link"); el.setAttribute("rel", rel); document.head.appendChild(el); }
      el.setAttribute("href", href);
    };
    const pageUrl = "https://fastpassportcenter.com/garland/";
    setLink("canonical", pageUrl);
    setMeta("description", "Emergency and expedited passport services in Garland, TX — as fast as 24 hours. Same-day courier processing available. A+ BBB rated, registered State Dept. courier serving Garland's diverse community, DFW corridor professionals, and school district travelers.");
    setMeta("geo.placename", "Garland, Texas");
    setMeta("geo.region", "US-TX");
    setMeta("geo.position", "32.9126;-96.6389");
    setMeta("ICBM", "32.9126, -96.6389");
    const servicesList = [
      { name: "Emergency Passport", description: "Urgent passport processing for Garland travelers with immediate departure needs." },
      { name: "Passport Renewal", description: "Fast passport renewal for Garland residents with recent passport issuance." },
      { name: "New Passport", description: "First-time application assistance for Garland adults and students." },
      { name: "Lost or Stolen Passport", description: "Passport replacement service for Garland residents." },
      { name: "Child Passport", description: "Passport services for minors under 16 in Garland." },
      { name: "Name Change", description: "Passport correction after marriage, divorce, or legal name changes." },
      { name: "Second Passport", description: "Second passport issuance for frequent Garland business travelers." },
    ];
    const graph = [
      { "@type": "BreadcrumbList", itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: "https://fastpassportcenter.com/" },
        { "@type": "ListItem", position: 2, name: "Texas", item: "https://fastpassportcenter.com/texas/" },
        { "@type": "ListItem", position: 3, name: "Garland Passport Services", item: pageUrl },
      ]},
      { "@type": "LocalBusiness", name: "Fast Passport Center", description: "Fast Passport Center does not have a physical office in Garland — we serve this area remotely. Emergency and expedited passport services as fast as 24 hours for Garland and DFW travelers.", url: pageUrl, telephone: "+1-877-253-0084", areaServed: { "@type": "City", name: "Garland", containedInPlace: { "@type": "State", name: "Texas" } }, priceRange: "$$$", openingHours: ["Mo-Fr 09:00-18:00"], aggregateRating: { "@type": "AggregateRating", ratingValue: "4.9", reviewCount: "1200" }},
      ...servicesList.map((s) => ({ "@type": "Service", serviceType: s.name, provider: { "@type": "LocalBusiness", name: "Fast Passport Center" }, areaServed: { "@type": "City", name: "Garland" }, description: s.description })),
      { "@type": "FAQPage", mainEntity: faqs.map((faq) => ({ "@type": "Question", name: faq.q, acceptedAnswer: { "@type": "Answer", text: faq.a } })) },
    ];
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "structured-data";
    document.getElementById("structured-data")?.remove();
    script.textContent = JSON.stringify({ "@context": "https://schema.org", "@graph": graph });
    document.head.appendChild(script);
    return () => {
      const existing = document.getElementById("structured-data");
      if (existing) existing.remove();
      const canonical = document.querySelector('link[rel="canonical"]');
      if (canonical) canonical.remove();
    };
  }, []);

  return (
    <div className="font-sans">
      <GarlandHeroSection />
      <GarlandStickyNav />
      <GarlandProcessingTimeSection />
      <GarlandServicesSection />
      <GarlandLocalAreaSection />
      <GarlandTravelTimingSection />
      <GarlandDestinationsSection />
      <GarlandTestimonialsSection />
      <GarlandAcceptanceFacilitiesSection />
      <GarlandComparisonSection />
      <GarlandMistakesSection />
      <GarlandHowItWorksSection />
      <GarlandFAQSection />
      <GarlandHelpfulResourcesSection />
      <SEOCrossLinks currentRoute="/cities/garland" state="TX" />
      <GarlandFinalCTA />
    </div>
  );
}